import { useCallback, useState } from "react";

import { invalidateCorridors } from "@/shared/hooks/useAvailableCorridors";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentTaxesService } from "../services/paymentTaxes.service";
import type { CreatePaymentTaxPayload, PaymentTax } from "../types/paymentTaxes.types";

export interface UseTaxMutationsResult {
  isSubmitting: boolean;
  error: string | null;
  clearError: () => void;
  createTax: (payload: CreatePaymentTaxPayload) => Promise<PaymentTax | null>;
  /** Deletes in order and stops at the first failure; a 404 counts as already gone. */
  removeTaxes: (ids: string[]) => Promise<boolean>;
}

export function useTaxMutations(): UseTaxMutationsResult {
  const { t } = useTranslations("paymentTaxes");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const messageFor = useCallback(
    (caught: unknown, fallback: "error_save" | "error_delete") =>
      HttpApiService.isAccessDeniedError(caught)
        ? t("error_no_write_permission")
        : HttpApiService.getErrorMessage(caught) || t(fallback),
    [t],
  );

  const createTax = useCallback(
    async (payload: CreatePaymentTaxPayload): Promise<PaymentTax | null> => {
      setIsSubmitting(true);
      setError(null);
      try {
        const created = await PaymentTaxesService.create(payload);
        invalidateCorridors();
        return created;
      } catch (caught: unknown) {
        setError(messageFor(caught, "error_save"));
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [messageFor],
  );

  const removeTaxes = useCallback(
    async (ids: string[]): Promise<boolean> => {
      setIsSubmitting(true);
      setError(null);
      try {
        for (const id of ids) {
          try {
            await PaymentTaxesService.remove(id);
          } catch (caught: unknown) {
            if (!HttpApiService.isNotFoundError(caught)) {
              setError(messageFor(caught, "error_delete"));
              return false;
            }
          }
        }
        return true;
      } finally {
        invalidateCorridors();
        setIsSubmitting(false);
      }
    },
    [messageFor],
  );

  return {
    isSubmitting,
    error,
    clearError: useCallback(() => setError(null), []),
    createTax,
    removeTaxes,
  };
}
